import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import moment from "moment";
import _ from "lodash";
import { PresetItem } from "../../models/PresetItem";
import {
  UsagePlanModel,
  UsagePlanItem,
} from "../../models/beginConstDoc/UsagePlanModel";
import { SafetyManagementItemData } from "../../constants/SafetyManagementItemData";
import { EnvironmentalItemData } from "../../constants/EnvironmentalItemData";
import { api } from "../../config/api";
import { util } from "../../config/util";
import AlertContext from "../../contexts/AlertContext";
import { useHistory } from "react-router-dom";
import { pages } from "../../constants/PagePaths";

interface UsagePlanFormProps {
  selectedItemId: string;
  selectedPresets: {
    fieldRepresentative?: PresetItem;
    companyInfo?: PresetItem;
    projectInfo?: PresetItem;
  };
  presets: PresetItem[];
  usagePlan: any;
  onUsagePlanChange: (data: any) => void;
  onSelectPreset: (
    type: "fieldRepresentative" | "companyInfo" | "projectInfo",
    preset?: PresetItem
  ) => void;
  onOpenPresetModal: (
    type: "fieldRepresentative" | "companyInfo" | "projectInfo"
  ) => void;
  onDocumentGenerate: (
    itemId: string,
    presets?: any,
    additionalData?: any
  ) => void;
  // 새로 추가된 props
  title: string;
  description: string;
  icon: string;
  cardTitle: string;
  cardDescription: string;
  saveButtonText?: string;
  // 로컬스토리지 함수들
  getDefaultPresetsFromStorage: () => any;
  saveDefaultPresetsToStorage: (defaultPresets: any) => void;
  // 모든 프리셋 초기화 함수
  onClearAllPresets?: () => void;
}

const UsagePlanForm: React.FC<UsagePlanFormProps> = ({
  selectedItemId,
  selectedPresets,
  presets,
  usagePlan,
  onUsagePlanChange,
  onSelectPreset,
  onOpenPresetModal,
  onDocumentGenerate,
  title,
  description,
  icon,
  cardTitle,
  cardDescription,
  saveButtonText = "저장",
  getDefaultPresetsFromStorage,
  saveDefaultPresetsToStorage,
  onClearAllPresets,
}) => {
  // 로딩 상태 관리
  const [isLoading, setIsLoading] = useState(false);
  // 기존 데이터 존재 여부 추적
  const [hasExistingData, setHasExistingData] = useState(false);
  // 기존 데이터의 ID 저장 (UPDATE용)
  const [existingDataId, setExistingDataId] = useState<string | null>(null);
  // 로컬 상태로 폼 데이터 관리 (usagePlanItems 또는 usageStateItems 둘 다 지원)
  const [localUsagePlan, setLocalUsagePlan] = useState<any>(
    usagePlan || { usagePlanItems: [] }
  );
  // 계상된 안전관리비 상태 관리
  const [estimatedSafetyFee, setEstimatedSafetyFee] = useState<string>("");
  // 프리셋 상태 변화 감지 및 로깅
  useEffect(() => {
    console.log("[PRESET_STATE_CHANGE] 프리셋 상태 변화 감지:", {
      companyInfo: selectedPresets.companyInfo,
      fieldRepresentative: selectedPresets.fieldRepresentative,
      projectInfo: selectedPresets.projectInfo,
    });
    console.log(
      "[PRESET_STATE_CHANGE] 로컬스토리지 상태:",
      getDefaultPresetsFromStorage()
    );
  }, [
    selectedPresets.companyInfo,
    selectedPresets.fieldRepresentative,
    selectedPresets.projectInfo,
  ]);

  // React hooks
  const alertContext = useContext(AlertContext);
  const history = useHistory();

  // 로그인 상태 확인 함수
  const checkLoginStatus = () => {
    if (!util.isLogin()) {
      alertContext.showAlert("로그인이 필요합니다.", () => {
        history.push(pages.login);
      });
      return false;
    }
    return true;
  };

  // 로컬스토리지에서 기본 프리셋 불러오기 및 자동 적용
  useEffect(() => {
    // presets가 로드되지 않았으면 실행하지 않음
    if (!presets || presets.length === 0) {
      return;
    }

    const storedDefaults = getDefaultPresetsFromStorage();

    // 로컬스토리지가 비어있으면 자동 적용하지 않음 (명시적으로 초기화된 경우)
    if (!storedDefaults || Object.keys(storedDefaults).length === 0) {
      console.log("[AUTO_APPLY] 로컬스토리지가 비어있음 - 자동 적용 생략");
      return;
    }

    console.log("[AUTO_APPLY] 로컬스토리지 기본 프리셋 자동 적용 시작");
    console.log("[AUTO_APPLY] 저장된 기본 프리셋:", storedDefaults);
    console.log("[AUTO_APPLY] 현재 선택된 프리셋:", selectedPresets);

    // 저장된 기본 프리셋이 있고, 현재 선택된 프리셋이 없는 경우 자동 적용
    if (storedDefaults.companyInfo && !selectedPresets.companyInfo) {
      const companyPreset = presets.find(
        (p) => p.id === storedDefaults.companyInfo?.id
      );
      if (companyPreset) {
        console.log("[AUTO_APPLY] 회사정보 자동 적용:", companyPreset.name);
        onSelectPreset("companyInfo", companyPreset);
      }
    }

    if (
      storedDefaults.fieldRepresentative &&
      !selectedPresets.fieldRepresentative
    ) {
      const fieldPreset = presets.find(
        (p) => p.id === storedDefaults.fieldRepresentative?.id
      );
      if (fieldPreset) {
        console.log("[AUTO_APPLY] 현장대리인 자동 적용:", fieldPreset.name);
        onSelectPreset("fieldRepresentative", fieldPreset);
      }
    }

    if (storedDefaults.projectInfo && !selectedPresets.projectInfo) {
      const projectPreset = presets.find(
        (p) => p.id === storedDefaults.projectInfo?.id
      );
      if (projectPreset) {
        console.log("[AUTO_APPLY] 공사정보 자동 적용:", projectPreset.name);
        onSelectPreset("projectInfo", projectPreset);
      }
    }
  }, [presets]);

  // 이전 회사정보 ID 추적용 ref
  const previousCompanyInfoIdRef = useRef<string | undefined>(undefined);

  // 회사정보 프리셋 변경 감지 및 의존 프리셋 초기화
  useEffect(() => {
    const currentCompanyInfoId = selectedPresets.companyInfo?.id;
    const previousCompanyInfoId = previousCompanyInfoIdRef.current;

    console.log("[COMPANY_CHANGE_CHECK]", {
      현재: currentCompanyInfoId,
      이전: previousCompanyInfoId,
      실제변경:
        previousCompanyInfoId !== undefined &&
        currentCompanyInfoId !== previousCompanyInfoId,
    });

    // 실제로 회사정보가 변경된 경우에만 의존 프리셋 초기화
    // (초기 마운트나 탭 이동은 제외)
    if (
      previousCompanyInfoId !== undefined && // 초기 마운트가 아니고
      currentCompanyInfoId !== previousCompanyInfoId && // 실제로 변경되었고
      (selectedPresets.fieldRepresentative || selectedPresets.projectInfo) // 의존 프리셋이 있는 경우
    ) {
      console.log("[PRESET] 실제 회사정보 변경 감지 - 의존 프리셋 초기화");

      // 현장대리인과 공사정보 선택 해제
      onSelectPreset("fieldRepresentative", undefined);
      onSelectPreset("projectInfo", undefined);

      // 폼 데이터도 템플릿으로 초기화
      const templateData = getTemplateData();
      setLocalUsagePlan({ [templateData.key]: templateData.items });
      setHasExistingData(false);
      setExistingDataId(null);
      setEstimatedSafetyFee("");

      // 로컬스토리지에서도 의존 프리셋 제거
      const currentDefaults = getDefaultPresetsFromStorage();
      const updatedDefaults = { ...currentDefaults };
      delete updatedDefaults.fieldRepresentative;
      delete updatedDefaults.projectInfo;
      saveDefaultPresetsToStorage(updatedDefaults);
    }

    // 현재 값을 이전 값으로 업데이트
    previousCompanyInfoIdRef.current = currentCompanyInfoId;
  }, [selectedPresets.companyInfo?.id]); // 회사정보 ID 변경 시에만 실행

  // 모든 프리셋 변경 시 폼 데이터 초기화
  useEffect(() => {
    console.log("[PRESET_CHANGE] 프리셋 변경 감지 - 폼 데이터 초기화");

    // 템플릿 데이터로 초기화
    const templateData = getTemplateData();
    setLocalUsagePlan({ [templateData.key]: templateData.items });
    setHasExistingData(false);
    setExistingDataId(null);
    setEstimatedSafetyFee("");

    // 부모 컴포넌트에도 변경사항 알림
    onUsagePlanChange({ [templateData.key]: templateData.items });
  }, [
    selectedPresets.companyInfo?.id,
    selectedPresets.fieldRepresentative?.id,
    selectedPresets.projectInfo?.id,
  ]); // 모든 프리셋 변경 시 폼 데이터 초기화

  // 프리셋 선택 취소 핸들러
  const handleClearPreset = useCallback(
    (type: "fieldRepresentative" | "companyInfo" | "projectInfo") => {
      console.log(`[CLEAR_PRESET] ${type} 프리셋 해제 시작`);
      console.log(`[CLEAR_PRESET] 현재 상태:`, {
        companyInfo: selectedPresets.companyInfo?.id,
        fieldRepresentative: selectedPresets.fieldRepresentative?.id,
        projectInfo: selectedPresets.projectInfo?.id,
      });

      // 회사정보 해제 시 모든 정보 초기화
      if (type === "companyInfo") {
        console.log("[CLEAR_PRESET] 회사정보 해제 - 모든 정보 초기화");

        // 새로운 함수가 있으면 사용, 없으면 기존 방식 사용
        if (onClearAllPresets) {
          console.log("[CLEAR_PRESET] onClearAllPresets 함수 사용");
          onClearAllPresets();
        } else {
          // 기존 방식: 로컬스토리지 먼저 전체 초기화
          console.log(
            "[CLEAR_PRESET] 로컬스토리지 초기화 전:",
            getDefaultPresetsFromStorage()
          );
          saveDefaultPresetsToStorage({});
          console.log(
            "[CLEAR_PRESET] 로컬스토리지 초기화 후:",
            getDefaultPresetsFromStorage()
          );

          // 모든 프리셋 해제 (한 번에 처리)
          console.log("[CLEAR_PRESET] onSelectPreset 호출 시작");
          onSelectPreset("companyInfo", undefined);
          onSelectPreset("fieldRepresentative", undefined);
          onSelectPreset("projectInfo", undefined);
          console.log("[CLEAR_PRESET] onSelectPreset 호출 완료");
        }

        // 폼 데이터 즉시 초기화
        console.log("[CLEAR_PRESET] 폼 데이터 즉시 초기화");
        const templateData = getTemplateData();
        setLocalUsagePlan({ [templateData.key]: templateData.items });
        setHasExistingData(false);
        setExistingDataId(null);
        setEstimatedSafetyFee("");

        // 부모 컴포넌트에도 변경사항 알림
        onUsagePlanChange({ [templateData.key]: templateData.items });

        console.log(
          "[CLEAR_PRESET] 회사정보 해제 - 모든 정보 및 로컬스토리지 초기화 완료"
        );
      } else {
        // 다른 프리셋 해제 시 해당 프리셋만 해제
        onSelectPreset(type, undefined);

        // 로컬스토리지에서 해당 프리셋만 제거
        const currentDefaults = getDefaultPresetsFromStorage();
        const updatedDefaults = { ...currentDefaults };
        delete updatedDefaults[type];
        saveDefaultPresetsToStorage(updatedDefaults);
      }

      console.log(`[CLEAR_PRESET] ${type} 프리셋 해제 완료`);
    },
    [
      selectedPresets,
      onSelectPreset,
      getDefaultPresetsFromStorage,
      saveDefaultPresetsToStorage,
      setLocalUsagePlan,
      onUsagePlanChange,
    ]
  );

  // API 데이터와 템플릿 병합 함수
  const mergeApiDataWithTemplate = (apiData: any[], isSafetyPlan: boolean) => {
    const templateData = getTemplateData();

    const mergedResult = templateData.items.map((templateItem: any) => {
      // API 데이터에서 같은 itemName을 가진 항목 찾기
      const apiItem = apiData.find(
        (item: any) => item.itemName === templateItem.itemName
      );

      if (apiItem) {
        // API 데이터가 있으면 병합
        const mergedItem = {
          ...templateItem,
          totalPrice: apiItem.totalPrice || 0,
          usageItems: templateItem.usageItems?.map(
            (templateUsageItem: any, index: number) => {
              const apiUsageItem = apiItem.usageItems?.[index];
              if (apiUsageItem) {
                const mergedUsageItem = {
                  ...templateUsageItem,
                  productName: apiUsageItem.productName || "",
                  productUnit: apiUsageItem.productUnit || "",
                  productNum: apiUsageItem.productNum || 0,
                  productPrice: apiUsageItem.productPrice || 0,
                  commaProductPrice: apiUsageItem.commaProductPrice || "0",
                  calPrice: apiUsageItem.calPrice || 0,
                  note: apiUsageItem.note || "",
                  usageDate: apiUsageItem.usageDate || "",
                  usageItemPrice: apiUsageItem.usageItemPrice || 0,
                };
                return mergedUsageItem;
              }
              return templateUsageItem;
            }
          ),
        };
        return mergedItem;
      }
      // API 데이터가 없으면 템플릿 그대로 사용
      return templateItem;
    });

    return mergedResult;
  };

  // selectedItemId에 따라 백엔드 요구 type 매핑
  const getBackendType = (itemId: string) => {
    switch (itemId) {
      case "start-2-2":
        return "safetyPlan";
      case "completion-2-2":
        return "safetyReport";
      case "start-2-3":
        return "environmentPlan";
      case "completion-2-3":
        return "environmentReport";
      default:
        return "unknown";
    }
  };

  // 내역서 → 계획서 type 매핑 함수
  const getPlanTypeFromReport = (reportItemId: string) => {
    switch (reportItemId) {
      case "completion-2-2": // 안전관리비 내역서
        return { itemId: "start-2-2", type: "safetyPlan" };
      case "completion-2-3": // 환경보전비 내역서
        return { itemId: "start-2-3", type: "environmentPlan" };
      default:
        return null;
    }
  };

  // 계획서 데이터로 내역서 폼 덮어씌우기 함수
  const loadPlanDataToReportForm = async (currentItemId: string) => {
    const planMapping = getPlanTypeFromReport(currentItemId);
    if (!planMapping) return false;

    console.log(`[FALLBACK] 내역서 데이터 없음 - 계획서 데이터 조회 시작`);
    console.log(
      `[FALLBACK] ${currentItemId} → ${planMapping.itemId} (${planMapping.type})`
    );

    try {
      const isSafetyPlan = currentItemId === "completion-2-2";
      let response;

      // 공사정보 ID 추출 (숫자 부분만)
      const projectInfoId = selectedPresets.projectInfo?.id
        ? Number(selectedPresets.projectInfo.id.split("-")[0])
        : undefined;

      if (isSafetyPlan) {
        response = await api.getSafetyPlanDocument(
          planMapping.type,
          projectInfoId
        );
      } else {
        response = await api.getEnvironmentPlanDocument(
          planMapping.type,
          projectInfoId
        );
      }

      // 계획서 데이터가 있는지 확인
      if (
        response.data &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        const planData = response.data.data[0];
        const formData =
          planData.form_data || planData.formData || planData.data;

        if (formData && formData.usageStateItems) {
          console.log(`[FALLBACK] 계획서 데이터 발견 - 내역서 폼에 덮어씌우기`);
          console.log(`[FALLBACK] 계획서 데이터:`, formData.usageStateItems);

          // 템플릿과 계획서 데이터 병합
          const mergedData = mergeApiDataWithTemplate(
            formData.usageStateItems,
            isSafetyPlan
          );

          // 폼에 계획서 데이터 적용 (저장하지 않음)
          setLocalUsagePlan({ usageStateItems: mergedData });
          onUsagePlanChange({ usageStateItems: mergedData });

          // 계획서의 계상된 관리비도 내역서에 적용
          if (formData.estimatedFee) {
            console.log(
              `[FALLBACK] 계획서의 계상된 관리비 적용:`,
              formData.estimatedFee
            );
            setEstimatedSafetyFee(formData.estimatedFee);
          }

          console.log(`[FALLBACK] 계획서 데이터로 폼 초기화 완료`);
          return true;
        }
      }

      console.log(`[FALLBACK] 계획서 데이터도 없음 - 템플릿으로 초기화`);
      return false;
    } catch (error) {
      console.error(`[FALLBACK] 계획서 데이터 조회 실패:`, error);
      return false;
    }
  };

  // API에서 기존 데이터 조회
  useEffect(() => {
    // selectedItemId가 변경될 때마다 상태 초기화
    console.log(`[ITEM_CHANGE] selectedItemId 변경: ${selectedItemId}`);
    console.log(`[ITEM_CHANGE] 현재 프리셋 상태:`, {
      companyInfo: selectedPresets.companyInfo?.id,
      fieldRepresentative: selectedPresets.fieldRepresentative?.id,
      projectInfo: selectedPresets.projectInfo?.id,
    });

    setHasExistingData(false);
    setExistingDataId(null);
    setEstimatedSafetyFee("");

    // 필요한 프리셋이 모두 선택되지 않았으면 데이터 로드하지 않음
    if (
      !selectedPresets.companyInfo ||
      !selectedPresets.fieldRepresentative ||
      !selectedPresets.projectInfo
    ) {
      console.log("[PRESET] 필수 프리셋이 선택되지 않음 - 데이터 로드 생략");
      // 템플릿 데이터로 초기화 (비활성화된 상태)
      const templateData = getTemplateData();
      setLocalUsagePlan({ [templateData.key]: templateData.items });
      return;
    }

    console.log(`[API_CALL] 데이터 로드 시작 - itemId: ${selectedItemId}`);

    const loadExistingData = async () => {
      const isSafetyPlan =
        selectedItemId === "start-2-2" || selectedItemId === "completion-2-2";
      const isEnvironmentPlan =
        selectedItemId === "start-2-3" || selectedItemId === "completion-2-3";

      if (isSafetyPlan) {
        try {
          setIsLoading(true);

          const currentType = getBackendType(selectedItemId);
          console.log(`[API] 안전관리비 조회 - type: ${currentType}`);

          // 공사정보 ID 추출 (숫자 부분만)
          const projectInfoId = selectedPresets.projectInfo?.id
            ? Number(selectedPresets.projectInfo.id.split("-")[0])
            : undefined;

          const response = await api.getSafetyPlanDocument(
            currentType,
            projectInfoId
          );

          // API 응답이 성공적이고 데이터가 있는지 확인
          if (
            response.data &&
            response.data.data &&
            response.data.data.length > 0
          ) {
            const existingData = response.data.data[0]; // 첫 번째 데이터 항목

            setHasExistingData(true);
            setExistingDataId(existingData.id || null);

            // 여러 가능한 키에서 form_data 찾기
            const formData =
              existingData.form_data ||
              existingData.formData ||
              existingData.data;

            if (formData && formData.usageStateItems) {
              // 템플릿과 API 데이터 병합
              const mergedData = mergeApiDataWithTemplate(
                formData.usageStateItems,
                true
              );

              setLocalUsagePlan({ usageStateItems: mergedData });
              onUsagePlanChange({ usageStateItems: mergedData });

              // 계상된 관리비 데이터 설정
              if (formData.estimatedFee) {
                setEstimatedSafetyFee(formData.estimatedFee);
              }
            } else {
              // 템플릿 데이터로 초기화
              const templateData = getTemplateData();
              setLocalUsagePlan({ [templateData.key]: templateData.items });
            }
          } else {
            setHasExistingData(false);
            setExistingDataId(null);

            // 내역서인 경우 계획서 데이터로 덮어씌우기 시도
            if (selectedItemId === "completion-2-2") {
              const fallbackSuccess = await loadPlanDataToReportForm(
                selectedItemId
              );
              if (!fallbackSuccess) {
                // 계획서 데이터도 없으면 템플릿으로 초기화
                const templateData = getTemplateData();
                setLocalUsagePlan({ [templateData.key]: templateData.items });
              }
            } else {
              // 계획서는 바로 템플릿으로 초기화
              const templateData = getTemplateData();
              setLocalUsagePlan({ [templateData.key]: templateData.items });
            }
          }
        } catch (error) {
          console.error("데이터 조회 실패:", error);
        } finally {
          setIsLoading(false);
        }
      } else if (isEnvironmentPlan) {
        try {
          setIsLoading(true);

          const currentType = getBackendType(selectedItemId);
          console.log(`[API] 환경보전비 조회 - type: ${currentType}`);

          // 공사정보 ID 추출 (숫자 부분만)
          const projectInfoId = selectedPresets.projectInfo?.id
            ? Number(selectedPresets.projectInfo.id.split("-")[0])
            : undefined;

          const response = await api.getEnvironmentPlanDocument(
            currentType,
            projectInfoId
          );

          // API 응답이 성공적이고 데이터가 있는지 확인
          if (
            response.data &&
            response.data.data &&
            response.data.data.length > 0
          ) {
            const existingData = response.data.data[0]; // 첫 번째 데이터 항목

            setHasExistingData(true);
            setExistingDataId(existingData.id || null);

            // 여러 가능한 키에서 form_data 찾기
            const formData =
              existingData.form_data ||
              existingData.formData ||
              existingData.data;

            if (formData && formData.usageStateItems) {
              // 템플릿과 API 데이터 병합
              const mergedData = mergeApiDataWithTemplate(
                formData.usageStateItems,
                false
              );

              setLocalUsagePlan({ usageStateItems: mergedData });
              onUsagePlanChange({ usageStateItems: mergedData });

              // 계상된 관리비 데이터 설정
              if (formData.estimatedFee) {
                setEstimatedSafetyFee(formData.estimatedFee);
              }
            } else {
              // 템플릿 데이터로 초기화
              const templateData = getTemplateData();
              setLocalUsagePlan({ [templateData.key]: templateData.items });
            }
          } else {
            setHasExistingData(false);
            setExistingDataId(null);

            // 내역서인 경우 계획서 데이터로 덮어씌우기 시도
            if (selectedItemId === "completion-2-3") {
              const fallbackSuccess = await loadPlanDataToReportForm(
                selectedItemId
              );
              if (!fallbackSuccess) {
                // 계획서 데이터도 없으면 템플릿으로 초기화
                const templateData = getTemplateData();
                setLocalUsagePlan({ [templateData.key]: templateData.items });
              }
            } else {
              // 계획서는 바로 템플릿으로 초기화
              const templateData = getTemplateData();
              setLocalUsagePlan({ [templateData.key]: templateData.items });
            }
          }
        } catch (error) {
          console.error("데이터 조회 실패:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadExistingData();
  }, [
    selectedItemId,
    selectedPresets.companyInfo?.id,
    selectedPresets.fieldRepresentative?.id,
    selectedPresets.projectInfo?.id,
  ]); // 모든 프리셋과 selectedItemId 변경 시 다시 로드

  // 프리셋 필수 여부 확인
  const isPresetsRequired =
    !selectedPresets.companyInfo ||
    !selectedPresets.fieldRepresentative ||
    !selectedPresets.projectInfo;

  // 고정된 템플릿 데이터 가져오기
  const getTemplateData = () => {
    const isSafetyPlan =
      selectedItemId === "start-2-2" || selectedItemId === "completion-2-2";
    const isEnvironmentPlan =
      selectedItemId === "start-2-3" || selectedItemId === "completion-2-3";

    if (isSafetyPlan) {
      // 안전관리비: usageStateItems 사용
      return {
        items: SafetyManagementItemData.usageStateItems || [],
        key: "usageStateItems",
      };
    } else if (isEnvironmentPlan) {
      // 환경보전비: usageStateItems 사용
      return {
        items: EnvironmentalItemData.usageStateItems || [],
        key: "usageStateItems",
      };
    }

    // 기본값
    return {
      items: [],
      key: "usagePlanItems",
    };
  };

  // 데이터 구조 처리
  const getItems = () => {
    // 로컬 상태에서 데이터 가져오기
    if (
      localUsagePlan &&
      (localUsagePlan.usagePlanItems || localUsagePlan.usageStateItems)
    ) {
      const items =
        localUsagePlan.usagePlanItems || localUsagePlan.usageStateItems || [];
      return items;
    }

    // 템플릿 데이터 사용
    const templateItems = getTemplateData().items;
    return templateItems;
  };

  const getItemsKey = () => {
    // 로컬 상태에서 데이터 구조 확인
    if (localUsagePlan && localUsagePlan.usagePlanItems) {
      return "usagePlanItems";
    } else if (localUsagePlan && localUsagePlan.usageStateItems) {
      return "usageStateItems";
    }

    // 템플릿 기반 키 사용
    return getTemplateData().key;
  };

  // 사용계획 관련 함수들
  const handleUsagePlanChange = (
    e: any,
    itemIndex: number,
    usageIndex: number
  ) => {
    const items = getItems();
    const newArr = items.map((planItem: UsagePlanItem, i: any) => {
      const cloneItem = _.cloneDeep(planItem);
      const cloneUsageItems = _.cloneDeep(planItem.usageItems);

      if (itemIndex === i) {
        const subItem: any = cloneUsageItems?.[usageIndex];
        if (e.target.name === "productPrice") {
          //productPrice일 때 콤마값과 아닌 값 저장.
          subItem &&
            (subItem["commaProductPrice"] = convertCommaValue(e.target.value));
          subItem &&
            (subItem["productPrice"] = +e.target.value.replaceAll(",", ""));
        } else {
          //각 input에 맞는 값이 입력
          subItem &&
            (subItem[e.target.name] = e.target.value.replaceAll(",", ""));
        }

        // 금액 계산: 수량 × 단가 (단위와 관계없이 항상 동일한 계산)
        const calPrice =
          Number(subItem.productNum || 0) * Number(subItem.productPrice || 0);
        subItem && (subItem["calPrice"] = calPrice);

        cloneUsageItems && (cloneUsageItems[usageIndex] = subItem);

        const usageItemsLength =
          cloneUsageItems === undefined ? 0 : cloneUsageItems?.length;
        let sumCalPrice = 0;
        for (let i = 0; i < usageItemsLength; i++) {
          let calPrice =
            cloneUsageItems === undefined ? 0 : cloneUsageItems[i].calPrice;
          sumCalPrice = sumCalPrice + calPrice;
        }
        cloneItem && (cloneItem["totalPrice"] = sumCalPrice); //각 항목의 금액 합계 적용
        return { ...cloneItem, usageItems: cloneUsageItems };
      } else {
        return cloneItem;
      }
    });

    const itemsKey = getItemsKey();
    const updatedUsagePlan = {
      ...localUsagePlan,
      [itemsKey]: newArr,
    };

    console.log(`[FORM] 폼 변경 - 항목:`, itemIndex, "사용항목:", usageIndex);
    console.log(`[FORM] 업데이트된 데이터:`, updatedUsagePlan);

    setLocalUsagePlan(updatedUsagePlan);
    onUsagePlanChange(updatedUsagePlan);
  };

  const handleUsagePlanSave = async (showAlert = true) => {
    // 로그인 상태 확인
    if (!checkLoginStatus()) {
      return;
    }

    try {
      setIsLoading(true);

      const isSafetyPlan =
        selectedItemId === "start-2-2" || selectedItemId === "completion-2-2";
      const isEnvironmentPlan =
        selectedItemId === "start-2-3" || selectedItemId === "completion-2-3";

      console.log(`[SAVE] 저장 시작 - ${title}`);
      console.log(`[SAVE] 기존 데이터 존재 여부:`, hasExistingData);
      console.log(`[SAVE] 기존 데이터 ID:`, existingDataId);

      // 현재 폼의 실제 데이터 가져오기 (localUsagePlan에서 직접)
      const currentFormData = getItems();
      const templateData = getTemplateData();

      console.log(`[SAVE] 현재 폼 데이터:`, currentFormData);
      console.log(`[SAVE] localUsagePlan:`, localUsagePlan);

      const backendType = getBackendType(selectedItemId);
      console.log(
        `[SAVE] selectedItemId: ${selectedItemId} → type: ${backendType}`
      );

      // 공사정보 ID 추출 (숫자 부분만)
      const projectInfoId = selectedPresets.projectInfo?.id
        ? selectedPresets.projectInfo.id.split("-")[0]
        : null;

      // 현재 폼 데이터를 그대로 사용 (사용자가 입력한 모든 변경사항 포함)
      const saveData = {
        itemId: selectedItemId,
        [templateData.key]: currentFormData, // 템플릿 병합 없이 현재 폼 데이터 직접 사용
        selectedPresets: selectedPresets,
        projectInfoId: projectInfoId, // 공사정보 ID 추가
        estimatedFee: estimatedSafetyFee, // 통일된 필드명으로 계상된 관리비 추가
        createdAt: new Date().toISOString(),
        type: backendType, // 백엔드 요구 형식으로 type 설정
      };

      console.log(`[SAVE] 저장할 데이터:`, saveData);

      let response;

      if (isSafetyPlan) {
        if (hasExistingData && existingDataId) {
          // 기존 데이터 UPDATE
          console.log(
            `[SAVE] 안전관리비 데이터 UPDATE 실행 - ID: ${existingDataId}`
          );
          response = await api.updateSafetyPlanDocument(
            Number(existingDataId),
            saveData
          );
          console.log(`[SAVE] 안전관리비 UPDATE 완료:`, response.data);
        } else {
          // 신규 데이터 CREATE
          console.log(`[SAVE] 안전관리비 데이터 CREATE 실행`);
          response = await api.createSafetyPlanDocument(saveData);
          console.log(`[SAVE] 안전관리비 CREATE 완료:`, response.data);
        }
      } else if (isEnvironmentPlan) {
        if (hasExistingData && existingDataId) {
          // 기존 데이터 UPDATE
          console.log(
            `[SAVE] 환경보전비 데이터 UPDATE 실행 - ID: ${existingDataId}`
          );
          response = await api.updateEnvironmentPlanDocument(
            Number(existingDataId),
            saveData
          );
          console.log(`[SAVE] 환경보전비 UPDATE 완료:`, response.data);
        } else {
          // 신규 데이터 CREATE
          console.log(`[SAVE] 환경보전비 데이터 CREATE 실행`);
          response = await api.createEnvironmentPlanDocument(saveData);
          console.log(`[SAVE] 환경보전비 CREATE 완료:`, response.data);
        }
      }

      if (response) {
        const action = hasExistingData ? "수정" : "저장";
        console.log(`[SAVE] ${action} 성공 - 응답:`, response.data);

        // showAlert가 true일 때만 알림 표시
        if (showAlert) {
          alert(`${title}이 ${action}되었습니다.`);
        }

        // 저장 후 기존 데이터 상태 업데이트
        if (!hasExistingData) {
          setHasExistingData(true);
          setExistingDataId(response.data.id || null);
          console.log(`[SAVE] 상태 업데이트 완료 - 새 ID:`, response.data.id);
        }

        // 저장 성공 반환
        return true;
      } else {
        // response가 없으면 저장 실패
        console.error(`[SAVE] 저장 실패 - 응답이 없음`);
        throw new Error("저장 응답이 없습니다.");
      }
    } catch (error) {
      console.error("저장 실패:", error);

      // showAlert가 true일 때만 알림 표시
      if (showAlert) {
        alert("저장에 실패했습니다. 다시 시도해주세요.");
      }

      // 에러를 다시 던져서 호출하는 쪽에서 처리할 수 있도록 함
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  function convertCommaValue(value: any) {
    value = Number(value.replaceAll(",", ""));
    if (isNaN(value)) {
      return 0;
    } else {
      const formatValue = value.toLocaleString("ko-KR");
      return formatValue;
    }
  }

  // 계상된 안전관리비 입력 핸들러
  const handleEstimatedSafetyFeeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value.replace(/[^0-9]/g, ""); // 숫자만 추출
    const formattedValue = value ? Number(value).toLocaleString("ko-KR") : "";
    setEstimatedSafetyFee(formattedValue);
  };

  return (
    <>
      {/* 기본 정보 설정 카드 */}
      <div
        className="card shadow-sm mb-4"
        style={{ border: "none", borderRadius: "16px" }}
      >
        <div
          className="card-header bg-white border-0 pt-3"
          style={{ borderRadius: "16px 16px 0 0" }}
        >
          <h5 className="card-title mb-0 d-flex align-items-center">
            <span className="material-symbols-outlined me-2 text-info">
              person_book
            </span>
            기본 정보 설정
          </h5>
          <p className="text-muted mb-0 mt-2">
            {description} 생성에 필요한 기본 정보를 설정해주세요.
          </p>
        </div>
        <div className="card-body">
          <div className="row g-3">
            {/* 회사정보 프리셋 */}
            <div className="col-md-4">
              <label className="form-label fw-semibold text-dark mb-2 d-flex align-items-center">
                <span
                  className="material-symbols-outlined me-2"
                  style={{ fontSize: "16px" }}
                >
                  business
                </span>
                회사정보
              </label>
              <div className="d-flex gap-2">
                <select
                  className="form-select"
                  value={selectedPresets.companyInfo?.id || ""}
                  onChange={(e) => {
                    const preset = presets.find(
                      (p) => p.id === e.target.value && p.type === "companyInfo"
                    );
                    onSelectPreset("companyInfo", preset);

                    // 선택된 프리셋을 기본 정보로 저장
                    if (preset) {
                      const currentDefaults = getDefaultPresetsFromStorage();
                      const updatedDefaults = {
                        ...currentDefaults,
                        companyInfo: preset,
                      };
                      saveDefaultPresetsToStorage(updatedDefaults);
                    }
                  }}
                >
                  <option value="">회사정보 선택</option>
                  {presets
                    .filter((p) => p.type === "companyInfo")
                    .map((preset) => (
                      <option key={preset.id} value={preset.id}>
                        {preset.name}
                      </option>
                    ))}
                </select>
                {selectedPresets.companyInfo && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm px-2"
                    onClick={() => {
                      console.log("[X_BUTTON] 회사정보 X버튼 클릭됨");
                      handleClearPreset("companyInfo");
                    }}
                    title="선택 취소"
                  >
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "16px" }}
                    >
                      close
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* 현장대리인 프리셋 */}
            <div className="col-md-4">
              <label className="form-label fw-semibold text-dark mb-2 d-flex align-items-center">
                <span
                  className="material-symbols-outlined me-2"
                  style={{ fontSize: "16px" }}
                >
                  engineering
                </span>
                현장대리인
              </label>
              <div className="d-flex gap-2">
                <select
                  className="form-select"
                  value={selectedPresets.fieldRepresentative?.id || ""}
                  onChange={(e) => {
                    const preset = presets.find(
                      (p) =>
                        p.id === e.target.value &&
                        p.type === "fieldRepresentative"
                    );
                    onSelectPreset("fieldRepresentative", preset);

                    // 선택된 프리셋을 기본 정보로 저장
                    if (preset) {
                      const currentDefaults = getDefaultPresetsFromStorage();
                      const updatedDefaults = {
                        ...currentDefaults,
                        fieldRepresentative: preset,
                      };
                      saveDefaultPresetsToStorage(updatedDefaults);
                    }
                  }}
                  disabled={!selectedPresets.companyInfo}
                >
                  <option value="">
                    {!selectedPresets.companyInfo
                      ? "회사정보를 먼저 선택하세요"
                      : "현장대리인 선택"}
                  </option>
                  {selectedPresets.companyInfo &&
                    presets
                      .filter(
                        (p) =>
                          p.type === "fieldRepresentative" &&
                          String((p.data as any).companyId) ===
                            selectedPresets.companyInfo?.id.split("-")[0]
                      )
                      .map((preset) => (
                        <option key={preset.id} value={preset.id}>
                          {preset.name}
                        </option>
                      ))}
                </select>
                {selectedPresets.fieldRepresentative && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm px-2"
                    onClick={() => handleClearPreset("fieldRepresentative")}
                    title="선택 취소"
                  >
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "16px" }}
                    >
                      close
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* 공사정보 프리셋 */}
            <div className="col-md-4">
              <label className="form-label fw-semibold text-dark mb-2 d-flex align-items-center">
                <span
                  className="material-symbols-outlined me-2"
                  style={{ fontSize: "16px" }}
                >
                  construction
                </span>
                공사정보
              </label>
              <div className="d-flex gap-2">
                <select
                  className="form-select"
                  value={selectedPresets.projectInfo?.id || ""}
                  onChange={(e) => {
                    const preset = presets.find(
                      (p) => p.id === e.target.value && p.type === "projectInfo"
                    );
                    onSelectPreset("projectInfo", preset);

                    // 선택된 프리셋을 기본 정보로 저장
                    if (preset) {
                      const currentDefaults = getDefaultPresetsFromStorage();
                      const updatedDefaults = {
                        ...currentDefaults,
                        projectInfo: preset,
                      };
                      saveDefaultPresetsToStorage(updatedDefaults);
                    }
                  }}
                  disabled={!selectedPresets.companyInfo}
                >
                  <option value="">
                    {!selectedPresets.companyInfo
                      ? "회사정보를 먼저 선택하세요"
                      : "공사정보 선택"}
                  </option>
                  {selectedPresets.companyInfo &&
                    presets
                      .filter(
                        (p) =>
                          p.type === "projectInfo" &&
                          String((p.data as any).companyId) ===
                            selectedPresets.companyInfo?.id.split("-")[0]
                      )
                      .map((preset) => (
                        <option key={preset.id} value={preset.id}>
                          {preset.name}
                        </option>
                      ))}
                </select>
                {selectedPresets.projectInfo && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm px-2"
                    onClick={() => handleClearPreset("projectInfo")}
                    title="선택 취소"
                  >
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "16px" }}
                    >
                      close
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 선택된 기본정보 미리보기 */}
          {(selectedPresets.fieldRepresentative ||
            selectedPresets.companyInfo ||
            selectedPresets.projectInfo) && (
            <div className="mt-4 p-3 bg-light rounded-3">
              <h6 className="fw-bold mb-2 text-dark d-flex align-items-center">
                <span
                  className="material-symbols-outlined me-2"
                  style={{ fontSize: "16px" }}
                >
                  preview
                </span>
                선택된 기본정보
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {selectedPresets.companyInfo && (
                  <span className="badge bg-warning text-dark">
                    <span
                      className="material-symbols-outlined me-1"
                      style={{ fontSize: "12px" }}
                    >
                      business
                    </span>
                    {selectedPresets.companyInfo.name}
                  </span>
                )}
                {selectedPresets.fieldRepresentative && (
                  <span className="badge bg-primary">
                    <span
                      className="material-symbols-outlined me-1"
                      style={{ fontSize: "12px" }}
                    >
                      engineering
                    </span>
                    {selectedPresets.fieldRepresentative.name}
                  </span>
                )}
                {selectedPresets.projectInfo && (
                  <span className="badge bg-success">
                    <span
                      className="material-symbols-outlined me-1"
                      style={{ fontSize: "12px" }}
                    >
                      construction
                    </span>
                    {selectedPresets.projectInfo.name}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* 기본정보관리 버튼 */}
          <div className="mt-4 pt-3 border-top">
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                <span
                  className="material-symbols-outlined me-1"
                  style={{ fontSize: "14px" }}
                >
                  info
                </span>
                기본 정보를 미리 설정하면 다음에 더 편리하게 사용할 수 있습니다.
              </small>
              <button
                onClick={() => onOpenPresetModal("companyInfo")}
                className="btn btn-outline-primary btn-sm d-flex align-items-center"
              >
                <span
                  className="material-symbols-outlined me-1"
                  style={{ fontSize: "16px" }}
                >
                  bookmark_manager
                </span>
                기본정보관리
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 사용계획 테이블 카드 */}
      <div
        className="card shadow-sm"
        style={{ border: "none", borderRadius: "16px" }}
      >
        <div
          className="card-header bg-white border-0 pt-3"
          style={{ borderRadius: "16px 16px 0 0" }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="card-title mb-0 d-flex align-items-center">
              <span
                className={`material-symbols-outlined me-2 ${
                  icon === "security" ? "text-success" : "text-warning"
                }`}
              >
                {icon}
              </span>
              {cardTitle}
            </h5>
            <div className="d-flex gap-2">
              <button
                onClick={() => handleUsagePlanSave()}
                className="btn btn-primary d-flex align-items-center"
                disabled={isLoading || isPresetsRequired}
              >
                <span className="material-symbols-outlined me-2">
                  {isLoading ? "sync" : "save"}
                </span>
                {isLoading ? "저장 중..." : saveButtonText}
              </button>
              <button
                className="btn btn-outline-secondary d-flex align-items-center"
                onClick={async () => {
                  // 로그인 상태 확인
                  if (!checkLoginStatus()) {
                    return;
                  }

                  try {
                    console.log("[DOWNLOAD] 다운로드 시작 - 저장 전 상태 확인");
                    console.log("[DOWNLOAD] 현재 폼 데이터:", localUsagePlan);
                    console.log(
                      "[DOWNLOAD] 계상된 관리비:",
                      estimatedSafetyFee
                    );

                    // 다운로드 전에 먼저 저장 (알림 없이)
                    console.log("[DOWNLOAD] 저장 시작");
                    await handleUsagePlanSave(false);
                    console.log("[DOWNLOAD] 저장 완료");

                    // 공사정보 ID 추출 (숫자 부분만)
                    const projectInfoId = selectedPresets.projectInfo?.id
                      ? selectedPresets.projectInfo.id.split("-")[0]
                      : null;

                    // 현재 폼의 최신 데이터 가져오기
                    const currentFormData = getItems();
                    const templateData = getTemplateData();

                    // 계상된 관리비 데이터, 공사정보 ID, 현재 폼 데이터 준비
                    const additionalData = {
                      estimatedFee: estimatedSafetyFee,
                      projectInfoId: projectInfoId,
                      formData: {
                        [templateData.key]: currentFormData,
                        estimatedFee: estimatedSafetyFee,
                      },
                    };

                    console.log(
                      "[DOWNLOAD] 다운로드 데이터 준비 완료:",
                      additionalData
                    );

                    // 그 다음 다운로드 (presets와 추가 데이터를 별개 파라미터로 전달)
                    onDocumentGenerate(
                      selectedItemId,
                      selectedPresets,
                      additionalData
                    );

                    console.log("[DOWNLOAD] 다운로드 함수 호출 완료");
                  } catch (error) {
                    console.error(
                      "[DOWNLOAD] 다운로드 과정에서 오류 발생:",
                      error
                    );
                    alert(
                      "다운로드 준비 중 오류가 발생했습니다. 다시 시도해주세요."
                    );
                  }
                }}
                disabled={isLoading || isPresetsRequired}
              >
                <span className="material-symbols-outlined me-2">download</span>
                다운로드
              </button>
            </div>
          </div>
          <div className="mt-2">
            <p className="text-muted mb-0">{cardDescription}</p>
          </div>
        </div>
        <div className="card-body">
          {/* 기본정보 미선택 시 안내 메시지 */}
          {isPresetsRequired && (
            <div className="text-center py-5">
              <div className="mb-3">
                <span
                  className="material-symbols-outlined text-warning"
                  style={{ fontSize: "48px" }}
                >
                  info
                </span>
              </div>
              <h5 className="text-dark mb-2">기본 정보를 먼저 설정해주세요</h5>
              <p className="text-muted mb-3">
                {title}을 작성하려면 위의 기본 정보 설정에서 <br />
                <strong>회사정보</strong>, <strong>현장대리인</strong>,{" "}
                <strong>공사정보</strong>를 모두 선택해야 합니다.
              </p>
              <div className="d-flex justify-content-center gap-2 flex-wrap">
                {!selectedPresets.companyInfo && (
                  <span className="badge bg-danger">회사정보 미선택</span>
                )}
                {!selectedPresets.fieldRepresentative && (
                  <span className="badge bg-danger">현장대리인 미선택</span>
                )}
                {!selectedPresets.projectInfo && (
                  <span className="badge bg-danger">공사정보 미선택</span>
                )}
              </div>
            </div>
          )}

          {/* 폼 영역 - 프리셋이 모두 선택된 경우에만 표시 */}
          {!isPresetsRequired && (
            <>
              {/* 로딩 상태 표시 */}
              {isLoading && (
                <div className="text-center py-4">
                  <div
                    className="spinner-border text-primary me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="text-muted">데이터를 불러오는 중...</span>
                </div>
              )}

              {!isLoading && (
                <>
                  {/* 계상된 안전관리비/환경보전비 입력 */}
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold d-flex align-items-center">
                        <span
                          className="material-symbols-outlined me-2 text-warning"
                          style={{ fontSize: "18px" }}
                        >
                          calculate
                        </span>
                        계상된{" "}
                        {selectedItemId.includes("start-2-2") ||
                        selectedItemId.includes("completion-2-2")
                          ? "안전관리비"
                          : "환경보전비"}
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={estimatedSafetyFee}
                          onChange={handleEstimatedSafetyFeeChange}
                          placeholder="금액을 입력하세요"
                          style={{ borderRadius: "8px 0 0 8px" }}
                        />
                        <span
                          className="input-group-text"
                          style={{ borderRadius: "0 8px 8px 0" }}
                        >
                          원
                        </span>
                      </div>
                      <small className="text-muted">
                        <span
                          className="material-symbols-outlined me-1"
                          style={{ fontSize: "12px" }}
                        >
                          info
                        </span>
                        계약서에 명시된{" "}
                        {selectedItemId.includes("start-2-2") ||
                        selectedItemId.includes("completion-2-2")
                          ? "안전관리비"
                          : "환경보전비"}{" "}
                        금액을 입력하세요.
                      </small>
                    </div>
                    <div className="col-md-6 d-flex align-items-end">
                      <div className="text-end w-100">
                        <h6 className="text-muted mb-0">
                          {moment().format("yyyy년 MM월")}
                        </h6>
                      </div>
                    </div>
                  </div>

                  {/* 사용계획 테이블 */}
                  <div className="table-responsive">
                    <div
                      className="d-flex border rounded-top fw-bold text-center small"
                      style={{
                        background:
                          "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                      }}
                    >
                      <div
                        className="p-3 border-end text-primary"
                        style={{ width: "23%" }}
                      >
                        품목
                      </div>
                      <div
                        className="p-3 border-end text-primary"
                        style={{ width: "15%" }}
                      >
                        단위
                      </div>
                      <div
                        className="p-3 border-end text-primary"
                        style={{ width: "12%" }}
                      >
                        수량
                      </div>
                      <div
                        className="p-3 border-end text-primary"
                        style={{ width: "15%" }}
                      >
                        단가
                      </div>
                      <div
                        className="p-3 border-end text-primary"
                        style={{ width: "23%" }}
                      >
                        금액
                      </div>
                      <div
                        className="p-3 text-primary"
                        style={{ width: "12%" }}
                      >
                        비고
                      </div>
                    </div>

                    {(() => {
                      const items = getItems();

                      if (!items || items.length === 0) {
                        return <div>데이터가 없습니다.</div>;
                      }

                      return items.map(
                        (item: UsagePlanItem, itemIndex: number) => {
                          return (
                            <div
                              key={itemIndex}
                              className="border border-top-0"
                            >
                              {/* 항목 헤더 */}
                              <div
                                className="d-flex"
                                style={{
                                  background:
                                    "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                                  border: "1px solid #2196f3",
                                }}
                              >
                                <div
                                  className="p-2 text-primary fw-bold d-flex align-items-center"
                                  style={{ width: "100%" }}
                                >
                                  <span
                                    className="badge bg-primary text-white me-2 px-2 py-1"
                                    style={{ fontSize: "12px" }}
                                  >
                                    {itemIndex + 1}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "14px",
                                      color: "#1565c0",
                                    }}
                                  >
                                    {item.itemName}
                                  </span>
                                </div>
                              </div>

                              {/* 데이터 행들 */}
                              <div>
                                {item.usageItems?.map(
                                  (usageItem: any, usageIndex: number) => (
                                    <div
                                      key={usageIndex}
                                      className="d-flex border-bottom"
                                      style={{
                                        backgroundColor:
                                          usageIndex % 2 === 0
                                            ? "#f8f9ff"
                                            : "#ffffff",
                                      }}
                                    >
                                      {/* 품목 */}
                                      <div
                                        className="p-1 border-end"
                                        style={{ width: "23%" }}
                                      >
                                        <input
                                          type="text"
                                          className="form-control form-control-sm w-100"
                                          style={{ fontSize: "12px" }}
                                          name="productName"
                                          value={usageItem.productName || ""}
                                          onChange={(e) =>
                                            handleUsagePlanChange(
                                              e,
                                              itemIndex,
                                              usageIndex
                                            )
                                          }
                                          placeholder="품목 입력"
                                        />
                                      </div>

                                      {/* 단위 */}
                                      <div
                                        className="p-1 border-end"
                                        style={{ width: "15%" }}
                                      >
                                        <input
                                          type="text"
                                          className="form-control form-control-sm w-100"
                                          style={{ fontSize: "12px" }}
                                          name="productUnit"
                                          value={usageItem.productUnit || ""}
                                          onChange={(e) =>
                                            handleUsagePlanChange(
                                              e,
                                              itemIndex,
                                              usageIndex
                                            )
                                          }
                                          placeholder="단위 입력"
                                        />
                                      </div>

                                      {/* 수량 */}
                                      <div
                                        className="p-1 border-end"
                                        style={{ width: "12%" }}
                                      >
                                        <input
                                          type="number"
                                          className="form-control form-control-sm text-center w-100"
                                          style={{ fontSize: "12px" }}
                                          name="productNum"
                                          value={usageItem.productNum || ""}
                                          onChange={(e) =>
                                            handleUsagePlanChange(
                                              e,
                                              itemIndex,
                                              usageIndex
                                            )
                                          }
                                          placeholder="수량 입력"
                                        />
                                      </div>

                                      {/* 단가 */}
                                      <div
                                        className="p-1 border-end"
                                        style={{ width: "15%" }}
                                      >
                                        <input
                                          type="text"
                                          className="form-control form-control-sm text-end w-100"
                                          style={{ fontSize: "12px" }}
                                          name="productPrice"
                                          value={
                                            usageItem.commaProductPrice || ""
                                          }
                                          onChange={(e) =>
                                            handleUsagePlanChange(
                                              e,
                                              itemIndex,
                                              usageIndex
                                            )
                                          }
                                          placeholder="단가 입력"
                                        />
                                      </div>

                                      {/* 금액 */}
                                      <div
                                        className="p-1 border-end"
                                        style={{ width: "23%" }}
                                      >
                                        <input
                                          type="text"
                                          className="form-control form-control-sm text-end w-100"
                                          style={{
                                            fontSize: "12px",
                                            backgroundColor: "#f8f9fa",
                                            cursor: "not-allowed",
                                          }}
                                          value={(
                                            usageItem.calPrice || 0
                                          ).toLocaleString()}
                                          readOnly
                                        />
                                      </div>

                                      {/* 비고 */}
                                      <div
                                        className="p-1"
                                        style={{ width: "12%" }}
                                      >
                                        <input
                                          type="text"
                                          className="form-control form-control-sm w-100"
                                          style={{ fontSize: "12px" }}
                                          name="note"
                                          value={usageItem.note || ""}
                                          onChange={(e) =>
                                            handleUsagePlanChange(
                                              e,
                                              itemIndex,
                                              usageIndex
                                            )
                                          }
                                          placeholder="비고 입력"
                                        />
                                      </div>
                                    </div>
                                  )
                                )}

                                {/* 소계 행 */}
                                <div
                                  className="d-flex"
                                  style={{ backgroundColor: "#e3f2fd" }}
                                >
                                  <div
                                    className="p-2 border-end fw-bold text-end text-primary"
                                    style={{ width: "65%" }}
                                  >
                                    소계
                                  </div>
                                  <div
                                    className="p-2 border-end text-end fw-bold text-primary"
                                    style={{ width: "23%" }}
                                  >
                                    {(item.totalPrice || 0).toLocaleString()}원
                                  </div>
                                  <div
                                    className="p-2"
                                    style={{ width: "12%" }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                      );
                    })()}
                  </div>

                  {/* 주의사항 */}
                  <div className="mt-4 p-3 bg-light rounded-3">
                    <div className="d-flex align-items-start">
                      <span className="material-symbols-outlined text-warning me-2">
                        info
                      </span>
                      <div>
                        <h6 className="fw-bold text-dark mb-1">작성 안내</h6>
                        <p className="text-muted mb-0 small">
                          ※ 주 : 사용계획은 항목별 사용일자가 빠른 순서로 작성
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default UsagePlanForm;
